import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TrendingUp, Users, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { UserDetailDialog } from './UserDetailDialog';
import { Button } from '@/components/ui/button';

interface UserGrowthData {
  date: string;
  caregivers: number;
  specialists: number;
  total: number;
}

interface TodayUser {
  id: string;
  name: string;
  email: string;
  type: 'caregiver' | 'specialist';
  created_at: string;
  specialistName?: string;
}

export function UserAnalytics() {
  const [growthData, setGrowthData] = useState<UserGrowthData[]>([]);
  const [todayUsers, setTodayUsers] = useState<TodayUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState({ caregivers: 0, specialists: 0, total: 0 });
  const [selectedUser, setSelectedUser] = useState<TodayUser | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch caregivers with profiles for email and specialist assignments
      const { data: caregivers, error: caregiversError } = await supabase
        .from('caregivers')
        .select(`
          id, 
          name, 
          auth_user_id, 
          created_at
        `)
        .order('created_at', { ascending: false });

      if (caregiversError) throw caregiversError;

      // Fetch specialist assignments
      const { data: assignments } = await supabase
        .from('specialist_caregiver')
        .select(`
          caregiver_id,
          specialist_id,
          specialists (
            name
          )
        `)
        .eq('status', 'active');

      // Create a map of caregiver_id to specialist name
      const specialistMap = new Map(
        (assignments || []).map(a => [
          a.caregiver_id, 
          a.specialists?.name || 'No specialist assigned'
        ])
      );

      // Fetch specialists
      const { data: specialists, error: specialistsError } = await supabase
        .from('specialists')
        .select('id, name, auth_user_id, email, created_at')
        .order('created_at', { ascending: false });

      if (specialistsError) throw specialistsError;

      // Fetch profiles to get caregiver emails
      const { data: profiles } = await supabase
        .from('profiles')
        .select('auth_user_id, email');

      const profilesMap = new Map(
        (profiles || []).map(p => [p.auth_user_id, p.email])
      );

      // Map caregivers with emails from profiles
      const caregiversWithEmails = (caregivers || []).map(caregiver => ({
        ...caregiver,
        email: profilesMap.get(caregiver.auth_user_id) || 'N/A'
      }));

      // Calculate daily growth for last 30 days
      const last30Days = Array.from({ length: 30 }, (_, i) => {
        const date = subDays(new Date(), 29 - i);
        const dateStr = format(date, 'yyyy-MM-dd');
        
        const caregiversOnDate = (caregivers || []).filter(c => 
          format(new Date(c.created_at), 'yyyy-MM-dd') === dateStr
        ).length;
        
        const specialistsOnDate = (specialists || []).filter(s => 
          format(new Date(s.created_at), 'yyyy-MM-dd') === dateStr
        ).length;

        return {
          date: format(date, 'MMM dd'),
          caregivers: caregiversOnDate,
          specialists: specialistsOnDate,
          total: caregiversOnDate + specialistsOnDate
        };
      });

      setGrowthData(last30Days);

      // Get users who joined today
      const today = new Date();
      const todayStart = startOfDay(today);
      const todayEnd = endOfDay(today);

      const todayCaregivers: TodayUser[] = (caregiversWithEmails || [])
        .filter(c => {
          const createdAt = new Date(c.created_at);
          return createdAt >= todayStart && createdAt <= todayEnd;
        })
        .map(c => ({
          id: c.id,
          name: c.name,
          email: c.email,
          type: 'caregiver' as const,
          created_at: c.created_at,
          specialistName: specialistMap.get(c.id)
        }));

      const todaySpecialists: TodayUser[] = (specialists || [])
        .filter(s => {
          const createdAt = new Date(s.created_at);
          return createdAt >= todayStart && createdAt <= todayEnd;
        })
        .map(s => ({
          id: s.id,
          name: s.name,
          email: s.email || 'N/A',
          type: 'specialist' as const,
          created_at: s.created_at
        }));

      setTodayUsers([...todayCaregivers, ...todaySpecialists].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ));

      // Calculate totals
      setTotalUsers({
        caregivers: caregivers?.length || 0,
        specialists: specialists?.length || 0,
        total: (caregivers?.length || 0) + (specialists?.length || 0)
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user analytics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              All registered users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Caregivers</CardTitle>
            <Users className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers.caregivers}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Total caregivers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Specialists</CardTitle>
            <Users className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers.specialists}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Total specialists
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Growth Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Daily User Growth (Last 30 Days)
          </CardTitle>
          <CardDescription>
            New user registrations per day
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  name="Total"
                />
                <Line 
                  type="monotone" 
                  dataKey="caregivers" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Caregivers"
                />
                <Line 
                  type="monotone" 
                  dataKey="specialists" 
                  stroke="#a855f7" 
                  strokeWidth={2}
                  name="Specialists"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Today's Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Users Joined Today
          </CardTitle>
          <CardDescription>
            {todayUsers.length} {todayUsers.length === 1 ? 'user' : 'users'} registered today
          </CardDescription>
        </CardHeader>
        <CardContent>
          {todayUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No new users today
            </div>
          ) : (
            <div className="space-y-3">
              {todayUsers.map(user => (
                <div 
                  key={user.id} 
                  onClick={() => {
                    setSelectedUser(user);
                    setDialogOpen(true);
                  }}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      user.type === 'caregiver' 
                        ? 'bg-blue-500/10 text-blue-500' 
                        : 'bg-purple-500/10 text-purple-500'
                    }`}>
                      <Users className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      {user.type === 'caregiver' && user.specialistName && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Specialist: <span className="font-medium">{user.specialistName}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.type === 'caregiver'
                        ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300'
                        : 'bg-purple-500/10 text-purple-700 dark:text-purple-300'
                    }`}>
                      {user.type}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(user.created_at), 'h:mm a')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Dialog */}
      <UserDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={selectedUser}
      />
    </div>
  );
}
